import { type Table, type Row, type Column } from '@tanstack/react-table';
import * as XLSX from 'xlsx';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function getHeaderLabel<TData>(column: Column<TData, unknown>): string {
  const h = column.columnDef.header;
  return typeof h === 'string' ? h : column.id;
}

function rowsToAoa<TData>(
  columns: Column<TData, unknown>[],
  rows: Row<TData>[],
): unknown[][] {
  const headers = columns.map(getHeaderLabel);
  const data = rows.map((row) =>
    columns.map((col) => {
      const val = row.getValue(col.id);
      return val === undefined || val === null ? '' : val;
    }),
  );
  return [headers, ...data];
}

function rowsToObjects<TData>(
  columns: Column<TData, unknown>[],
  rows: Row<TData>[],
): Record<string, unknown>[] {
  return rows.map((row) => {
    const obj: Record<string, unknown> = {};
    for (const col of columns) {
      const key = getHeaderLabel(col);
      const val = row.getValue(col.id);
      obj[key] = val === undefined || val === null ? '' : val;
    }
    return obj;
  });
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function buildCSVBlob<TData>(
  columns: Column<TData, unknown>[],
  rows: Row<TData>[],
): Blob {
  const aoa = rowsToAoa(columns, rows);
  const csv = aoa
    .map((row) =>
      row
        .map((cell) => {
          const str = String(cell);
          // Wrap in quotes if the value contains a comma, quote, or newline
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        })
        .join(','),
    )
    .join('\n');

  // BOM for UTF-8
  const bom = '\uFEFF';
  return new Blob([bom + csv], { type: 'text/csv;charset=utf-8' });
}

function buildXLSXBuffer<TData>(
  columns: Column<TData, unknown>[],
  rows: Row<TData>[],
): ArrayBuffer {
  const objects = rowsToObjects(columns, rows);
  const headers = columns.map(getHeaderLabel);
  const ws = XLSX.utils.json_to_sheet(objects, { header: headers });

  // Auto-size columns based on max content length
  ws['!cols'] = headers.map((h, i) => {
    let maxLen = h.length;
    for (const obj of objects) {
      const val = String(obj[h] ?? '');
      if (val.length > maxLen) maxLen = val.length;
    }
    return { wch: Math.min(maxLen + 2, 60) };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  return XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
}

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */

/** Export all filtered rows as CSV */
export function exportToCSV<TData>(
  table: Table<TData>,
  filename = 'export',
): void {
  const columns = table.getVisibleLeafColumns();
  const rows = table.getFilteredRowModel().rows;
  const blob = buildCSVBlob(columns, rows);
  triggerDownload(blob, `${filename}.csv`);
}

/** Export all filtered rows as XLSX */
export function exportToXLSX<TData>(
  table: Table<TData>,
  filename = 'export',
): void {
  const columns = table.getVisibleLeafColumns();
  const rows = table.getFilteredRowModel().rows;
  const buffer = buildXLSXBuffer(columns, rows);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${filename}.xlsx`);
}

/** Export only selected rows as CSV */
export function exportSelectedToCSV<TData>(
  table: Table<TData>,
  filename = 'export-selected',
): void {
  const columns = table.getVisibleLeafColumns();
  const rows = table.getSelectedRowModel().rows;
  const blob = buildCSVBlob(columns, rows);
  triggerDownload(blob, `${filename}.csv`);
}

/** Export only selected rows as XLSX */
export function exportSelectedToXLSX<TData>(
  table: Table<TData>,
  filename = 'export-selected',
): void {
  const columns = table.getVisibleLeafColumns();
  const rows = table.getSelectedRowModel().rows;
  const buffer = buildXLSXBuffer(columns, rows);
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${filename}.xlsx`);
}

/** Generic export with explicit column + row selection (used by CustomExportDialog) */
export function exportCustom<TData>(
  table: Table<TData>,
  opts: {
    columnIds: string[];
    rowSource: 'all' | 'selected' | 'page';
    format: 'csv' | 'xlsx';
    filename: string;
  },
): void {
  const allColumns = table.getVisibleLeafColumns();
  const columns = allColumns.filter((c) => opts.columnIds.includes(c.id));

  let rows: Row<TData>[];
  switch (opts.rowSource) {
    case 'selected':
      rows = table.getSelectedRowModel().rows;
      break;
    case 'page':
      rows = table.getRowModel().rows;
      break;
    default:
      rows = table.getFilteredRowModel().rows;
  }

  if (opts.format === 'xlsx') {
    const buffer = buildXLSXBuffer(columns, rows);
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    triggerDownload(blob, `${opts.filename}.xlsx`);
  } else {
    const blob = buildCSVBlob(columns, rows);
    triggerDownload(blob, `${opts.filename}.csv`);
  }
}
